import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Box, MenuItem, Stack, Tabs as MuiTabs } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Page from '../../components/shared/Page/Page';
import StyledContentLayout from '../../styles/StyledContentLayout';
import StyledTab from '../../components/shared/Tabs/StyledTab';
import StyledConfettiExplosion from '../../components/agents/AgentForm/StyledConfettiExplosion';
import {
  pricingPlansCall,
  pricingPlansChat,
  pricingPlansBundle,
} from '../../config/billingPlansConfig';
import PricingCardsCarousel from '../../components/pricing/PricingCardsCarousel';
import PricingCard from '../../components/pricing/PricingCard';
import PricingTierSelect from '../../components/pricing/PricingTierSelect';
import useAuth from '../../hooks/useAuth';
import { useSubscription } from '../../contexes/SubscriptionProvider';
import { shouldShowChatUserCallEnterpriseNotice } from '../../helpers/pricingUpgradeRules';
import { priceToPerMoShort } from '../../helpers/pricingCardCopy';
import { Price } from '../../interfaces/Price';
import { useTranslation } from '../../hooks/useTranslation';
import { bundleTierCapacityMenuSegment } from '../../helpers/billingTierMenuLabels';
import {
  getVisibleV2PlanTabs,
  isPaidPlanSubscriber,
  type V2PlanTab,
} from '../../helpers/billingPlanSwitchUi';
import { changeSubscriptionPlan } from '../../services/billingService';
import { useSnackBar } from '../../hooks/useSnackBar';
import { USAGE_DATA_REFRESH_EVENT } from '../../helpers/usageEvents';
import { enforcePlanAgentLimits } from '../../helpers/enforcePlanAgentLimits';
import { ConfettiProps } from 'react-confetti-explosion';

const ENTERPRISE_REQUIRED_FOR_CHAT_TO_CALLS =
  'Enterprise is required to add calls to your chat subscription.' as const;

const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { company, subscription, refreshSubscription } = useSubscription();
  const showSnackBar = useSnackBar();
  const [v2Tab, setV2Tab] = useState<'call' | 'chat' | 'bundle'>('call');
  const [switchingPlanId, setSwitchingPlanId] = useState<string | null>(null);
  const [optimisticPlanId, setOptimisticPlanId] = useState<string | null>(null);
  const [confettiBurst, setConfettiBurst] = useState(0);
  const defaultTabApplied = useRef(false);
  const effectiveCurrentPlanId = optimisticPlanId ?? company?.plan_id ?? null;

  useEffect(() => {
    if (company?.plan_id) {
      setOptimisticPlanId(company.plan_id);
    }
  }, [company?.plan_id]);

  const visibleTabs = useMemo(
    () => getVisibleV2PlanTabs(company, subscription),
    [company, subscription]
  );

  useEffect(() => {
    if (!visibleTabs.includes(v2Tab)) {
      setV2Tab(visibleTabs[0] ?? 'call');
    }
  }, [visibleTabs, v2Tab]);

  const effectivePlanTab = visibleTabs.includes(v2Tab)
    ? v2Tab
    : (visibleTabs[0] ?? 'call');

  const paidForSwitch = isPaidPlanSubscriber(company, subscription);

  const handleSwitchPlan = useCallback(
    async (plan: Price) => {
      if (!currentUser?.id) return;
      setSwitchingPlanId(plan.id);
      try {
        await changeSubscriptionPlan(
          currentUser.id,
          plan.id,
          currentUser.company_id
        );
        await enforcePlanAgentLimits(currentUser);
        setOptimisticPlanId(plan.id);
        setConfettiBurst(prev => prev + 1);
        window.dispatchEvent(new Event(USAGE_DATA_REFRESH_EVENT));
        showSnackBar(t('Plan updated successfully'), 'success');
        await refreshSubscription();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : t('Error');
        showSnackBar(msg, 'error');
      } finally {
        setSwitchingPlanId(null);
      }
    },
    [currentUser?.id, refreshSubscription, showSnackBar, t]
  );

  const planSwitchContext =
    paidForSwitch && currentUser
      ? {
          enabled: true,
          currentPlanId: effectiveCurrentPlanId,
          switchingPlanId,
          onSwitchPlan: handleSwitchPlan,
        }
      : undefined;

  const activeV2Plans = useMemo<Price[]>(
    () =>
      (effectivePlanTab === 'call'
        ? pricingPlansCall
        : effectivePlanTab === 'chat'
          ? pricingPlansChat
          : pricingPlansBundle) as Price[],
    [effectivePlanTab]
  );

  const enterprisePlan = useMemo(
    () => activeV2Plans.find(p => p.tier === 'enterprise') ?? null,
    [activeV2Plans]
  );

  const starterPlans = useMemo(
    () => activeV2Plans.filter(p => p.tier === 'starter'),
    [activeV2Plans]
  );
  const businessPlans = useMemo(
    () => activeV2Plans.filter(p => p.tier === 'business'),
    [activeV2Plans]
  );

  const getTierNum = (id: string) => {
    const match = id.match(/_t(\d+)$/);
    return match ? Number(match[1]) : 0;
  };

  const sortedStarterPlans = useMemo(
    () => [...starterPlans].sort((a, b) => getTierNum(a.id) - getTierNum(b.id)),
    [starterPlans]
  );
  const sortedBusinessPlans = useMemo(
    () =>
      [...businessPlans].sort((a, b) => getTierNum(a.id) - getTierNum(b.id)),
    [businessPlans]
  );

  const starterDefaultPlanId = sortedStarterPlans[0]?.id ?? '';
  const businessDefaultPlanId = sortedBusinessPlans[0]?.id ?? '';

  const [selectedStarterPlanId, setSelectedStarterPlanId] =
    useState<string>(starterDefaultPlanId);
  const [selectedBusinessPlanId, setSelectedBusinessPlanId] = useState<string>(
    businessDefaultPlanId
  );

  useEffect(() => {
    setSelectedStarterPlanId(starterDefaultPlanId);
    setSelectedBusinessPlanId(businessDefaultPlanId);
  }, [starterDefaultPlanId, businessDefaultPlanId, effectivePlanTab]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('PlanTab') as V2PlanTab | null;
    if (
      fromUrl &&
      (fromUrl === 'call' || fromUrl === 'chat' || fromUrl === 'bundle') &&
      visibleTabs.includes(fromUrl)
    ) {
      setV2Tab(fromUrl);
      defaultTabApplied.current = true;
      return;
    }
    if (
      !defaultTabApplied.current &&
      (company?.plan_type === 'chat' ||
        company?.plan_type === 'call' ||
        company?.plan_type === 'bundle')
    ) {
      const p = company.plan_type;
      if (p && visibleTabs.includes(p)) {
        setV2Tab(p);
      }
      defaultTabApplied.current = true;
    }
  }, [location.search, company?.plan_type, visibleTabs]);

  const starterStartingAt = useMemo(
    () => priceToPerMoShort(sortedStarterPlans[0]?.price ?? ''),
    [sortedStarterPlans]
  );
  const businessStartingAt = useMemo(
    () => priceToPerMoShort(sortedBusinessPlans[0]?.price ?? ''),
    [sortedBusinessPlans]
  );
  const confettiProps: ConfettiProps = {
    force: 0.6,
    duration: 2500,
    particleCount: 180,
    width: 1200,
    colors: ['#041E43', '#1471BF', '#5BB4DC', '#FC027B', '#66D805'],
  };

  return (
    <Page title="Elora | Pricing">
      <StyledContentLayout wide>
        {confettiBurst > 0 && (
          <StyledConfettiExplosion key={confettiBurst} {...confettiProps} />
        )}
        <Stack spacing={3}>
          <Box>
            <h1>{t('Pricing plans')}</h1>
          </Box>
          {shouldShowChatUserCallEnterpriseNotice(
            company,
            effectivePlanTab
          ) && (
            <Alert severity="info">
              {t(ENTERPRISE_REQUIRED_FOR_CHAT_TO_CALLS)}
            </Alert>
          )}
          <Box sx={{ width: '100%' }}>
            <MuiTabs
              value={Math.max(0, visibleTabs.indexOf(effectivePlanTab))}
              onChange={(_, index: number) => {
                const next = visibleTabs[index];
                if (next) setV2Tab(next);
              }}
              variant="fullWidth"
              aria-label={t('Plan type')}
              sx={{
                p: 0.5,
                borderRadius: 999,
                bgcolor: '#FFFFFF',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.08)',
                minHeight: 56,
                '& .MuiTabs-indicator': { display: 'none' },
              }}
            >
              {visibleTabs.map(tab => (
                <StyledTab
                  key={tab}
                  label={
                    tab === 'call'
                      ? t('Call')
                      : tab === 'chat'
                        ? t('Chat')
                        : t('Bundle')
                  }
                />
              ))}
            </MuiTabs>
          </Box>
          <PricingCardsCarousel
            key={effectivePlanTab}
            scrollToPlanId={effectiveCurrentPlanId}
          >
            {sortedStarterPlans.length > 0 && (
              <PricingCard
                plan={
                  (sortedStarterPlans.find(
                    p => p.id === selectedStarterPlanId
                  ) ?? sortedStarterPlans[0]) as Price
                }
                currentUser={currentUser}
                upgradeContext={{
                  activeV2Tab: effectivePlanTab,
                  company,
                }}
                planSwitchContext={planSwitchContext}
                startingAtMonthly={starterStartingAt}
                tierSelector={
                  <PricingTierSelect
                    value={selectedStarterPlanId}
                    onChange={v => setSelectedStarterPlanId(v)}
                  >
                    {sortedStarterPlans.map(plan => {
                      const tierNum = getTierNum(plan.id);
                      const label =
                        effectivePlanTab === 'call'
                          ? `Starter T${tierNum} (${plan.minutes ?? 0} min)`
                          : effectivePlanTab === 'chat'
                            ? `Starter T${tierNum} (${plan.messages ?? 0} msgs)`
                            : `Starter T${tierNum} (${bundleTierCapacityMenuSegment(
                                t,
                                plan
                              )})`;

                      return (
                        <MenuItem key={plan.id} value={plan.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </PricingTierSelect>
                }
              />
            )}

            {sortedBusinessPlans.length > 0 && (
              <PricingCard
                plan={
                  (sortedBusinessPlans.find(
                    p => p.id === selectedBusinessPlanId
                  ) ?? sortedBusinessPlans[0]) as Price
                }
                currentUser={currentUser}
                upgradeContext={{
                  activeV2Tab: effectivePlanTab,
                  company,
                }}
                planSwitchContext={planSwitchContext}
                startingAtMonthly={businessStartingAt}
                tierSelector={
                  <PricingTierSelect
                    value={selectedBusinessPlanId}
                    onChange={v => setSelectedBusinessPlanId(v)}
                  >
                    {sortedBusinessPlans.map(plan => {
                      const tierNum = getTierNum(plan.id);
                      const label =
                        effectivePlanTab === 'call'
                          ? `Business T${tierNum} (${plan.minutes ?? 0} min)`
                          : effectivePlanTab === 'chat'
                            ? `Business T${tierNum} (${plan.messages ?? 0} msgs)`
                            : `Business T${tierNum} (${bundleTierCapacityMenuSegment(
                                t,
                                plan
                              )})`;

                      return (
                        <MenuItem key={plan.id} value={plan.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </PricingTierSelect>
                }
              />
            )}

            {enterprisePlan && (
              <PricingCard
                plan={enterprisePlan as Price}
                currentUser={currentUser}
                upgradeContext={{
                  activeV2Tab: effectivePlanTab,
                  company,
                }}
              />
            )}
          </PricingCardsCarousel>
        </Stack>
      </StyledContentLayout>
    </Page>
  );
};

export default Pricing;
